import React from 'react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDockMagnify, type DockRise } from '@/components/toolbar/useDockMagnify';
import { DockGlyph } from '@/components/toolbar/DockGlyph';

// One look for every floating canvas control (zoom, fit, home, boards,
// Partnr), matching the bottom toolbar: a frosted pill holding round 40px
// buttons, a soft fill on hover, a dark fill when active, a small press, and
// the dock's distance-based magnification on hover.

/** The frosted pill that holds one or more controls. */
export function CanvasControlGroup({
  className,
  style,
  rise = 'left',
  children,
}: {
  className?: string;
  style?: React.CSSProperties;
  /** Which way hovered glyphs rise: away from the screen edge the group sits on. */
  rise?: DockRise;
  children: React.ReactNode;
}) {
  const dock = useDockMagnify<HTMLDivElement>({ rise });
  return (
    <div ref={dock.ref} {...dock.handlers} className={cn('floating-pill flex flex-col gap-0.5 rounded-full p-1', className)} style={style}>
      {children}
    </div>
  );
}

interface CanvasControlProps {
  label: string;
  /** Shown after the label in the tooltip, e.g. "+" or "1". */
  shortcut?: string;
  side?: 'left' | 'right' | 'top' | 'bottom';
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

export function CanvasControl({ label, shortcut, side = 'left', active, disabled, onClick, children }: CanvasControlProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-pressed={active}
          disabled={disabled}
          onClick={onClick}
          data-dock-item
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full',
            'transition-[background-color,color,transform] duration-150 active:scale-95',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:pointer-events-none disabled:opacity-40',
            active
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'text-foreground hover:bg-muted/60',
          )}
        >
          <DockGlyph>{children}</DockGlyph>
        </button>
      </TooltipTrigger>
      <TooltipContent side={side}>
        <p>
          {label}
          {shortcut && <span className="ml-2 text-muted-foreground">{shortcut}</span>}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

/** Same size and stroke for every control icon. */
export const CONTROL_ICON = 'h-5 w-5';
